/**
 * Browser API guards and safe wrappers for defensive programming
 * Prevents runtime errors in environments with restricted or missing APIs
 */

// Environment detection
export const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
export const hasIDB = isBrowser && 'indexedDB' in window && window.indexedDB !== null;
export const hasLS = isBrowser && 'localStorage' in window && window.localStorage !== null;
export const hasBeacon = typeof navigator !== 'undefined' && 'sendBeacon' in navigator;
export const hasCryptoUUID = typeof crypto !== 'undefined' && crypto !== null && 'randomUUID' in crypto;
export const hasAbortController = typeof AbortController !== 'undefined';

// Safe localStorage wrapper
export const safeLocalStorage = {
  get(key) {
    if (!hasLS) return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('localStorage.getItem failed:', error);
      return null;
    }
  },
  
  set(key, value) {
    if (!hasLS) return false;
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn('localStorage.setItem failed:', error);
      return false;
    }
  },
  
  remove(key) {
    if (!hasLS) return false;
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('localStorage.removeItem failed:', error);
      return false;
    }
  }
};

// Safe sendBeacon with fetch fallback
export function keepAlive(url, payload) {
  if (!isBrowser) return Promise.resolve(false);
  
  const body = JSON.stringify(payload);
  const headers = { 'Content-Type': 'application/json' };
  
  // Try sendBeacon first (most reliable for unload events)
  if (hasBeacon) {
    try {
      const success = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      if (success) {
        console.log('Beacon sent successfully');
        return Promise.resolve(true);
      }
    } catch (error) {
      console.warn('sendBeacon failed, falling back to fetch:', error);
    }
  }
  
  // Fallback to fetch with keepalive
  try {
    return fetch(url, { 
      method: 'POST', 
      body, 
      keepalive: true, 
      headers 
    }).then(() => {
      console.log('Keepalive fetch sent successfully');
      return true;
    }).catch((error) => {
      console.warn('Keepalive fetch failed:', error);
      return false;
    });
  } catch (error) {
    console.warn('Fetch not available:', error);
    return Promise.resolve(false);
  }
}

// Safe IndexedDB wrapper
export async function openIDB(name, version = 1, upgradeCallback = null) {
  if (!hasIDB) {
    console.warn('IndexedDB not available');
    return null;
  }
  
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(name, version);
      
      request.onsuccess = () => {
        console.log('IndexedDB opened successfully');
        resolve(request.result);
      };
      
      request.onerror = (event) => {
        console.warn('IndexedDB open failed:', request.error);
        resolve(null);
      };
      
      request.onblocked = () => {
        console.warn('IndexedDB open blocked');
        resolve(null);
      };
      
      request.onupgradeneeded = (event) => {
        try {
          if (upgradeCallback) {
            upgradeCallback(event.target.result, event);
          }
        } catch (error) {
          console.error('IndexedDB upgrade failed:', error);
          resolve(null);
        }
      };
    } catch (error) {
      console.warn('IndexedDB open exception:', error);
      resolve(null);
    }
  });
}

// Safe IDB transaction wrapper
export async function safeIDBTransaction(db, storeName, mode, operation) {
  if (!db) {
    console.warn('Database not available for transaction');
    return null;
  }
  
  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([storeName], mode);
      const store = transaction.objectStore(storeName);
      
      transaction.oncomplete = () => {
        console.log('Transaction completed successfully');
      };
      
      transaction.onerror = (event) => {
        console.warn('Transaction failed:', transaction.error);
        resolve(null);
      };
      
      transaction.onabort = () => {
        console.warn('Transaction aborted');
        resolve(null);
      };
      
      const request = operation(store);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.warn('Store operation failed:', request.error);
        resolve(null);
      };
    } catch (error) {
      console.warn('Transaction creation failed:', error);
      resolve(null);
    }
  });
}

// Generate safe UUID
export function generateUUID() {
  if (hasCryptoUUID) {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.warn('crypto.randomUUID failed:', error);
    }
  }
  
  // Fallback UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Safe event listener management
export function safeAddEventListener(target, event, handler, options = {}) {
  if (!isBrowser || !target || typeof target.addEventListener !== 'function') {
    return () => {}; // Return no-op cleanup function
  }
  
  try {
    target.addEventListener(event, handler, options);
    return () => {
      try {
        target.removeEventListener(event, handler, options);
      } catch (error) {
        console.warn('Event listener removal failed:', error);
      }
    };
  } catch (error) {
    console.warn('Event listener addition failed:', error);
    return () => {};
  }
}

// Safe focus management
export function safeFocus(element) {
  if (!isBrowser || !element || typeof element.focus !== 'function') {
    return false;
  }
  
  try {
    element.focus();
    return true;
  } catch (error) {
    console.warn('Focus failed:', error);
    return false;
  }
}

// Check if we're in a supported environment
export function checkEnvironment() {
  const checks = {
    browser: isBrowser,
    indexedDB: hasIDB,
    localStorage: hasLS,
    sendBeacon: hasBeacon,
    crypto: hasCryptoUUID,
    abortController: hasAbortController
  };
  
  console.log('Environment check:', checks);
  return checks;
}